import { memo, useCallback, useMemo, useState } from 'react';
import ProductLimitedList from '~/components/ProductLimitedList';
import useProductCollections from '~/hooks/useProductCollections';
import Container from '~/components/Container';
import Typography from '~/components/Typography';
import ChipButtonGroup from '~/components/ChipButtonGroup';
import { Product } from '~/services/kup/models/Product';
import { extractProductEventData } from '~/utils/product';
import useBatchLogger from '~/hooks/useBatchLogger';
import { ProductCollection } from '~/services/kup/models/ProductCollection';
import Skeleton from '@mui/material/Skeleton';
import Box from '@mui/material/Box';
import { getProductCollectionProducts } from '~kup/controllers/productCollection.ts';

export default memo(MainRankingList);

function MainRankingList() {
  const { logAction } = useBatchLogger();
  const { collectionGroup } = useProductCollections({
    slug: 'gift-recommendations-collection-group',
  });

  const [rankingCollection, setRankingCollection] = useState<ProductCollection | null>(null);

  const buttonInfos = useMemo(
    () =>
      collectionGroup?.collections?.map((collection) => ({
        label: collection.name ?? '',
        key: collection.slug ?? '',
      })),
    [collectionGroup]
  );

  const rankingProducts = useMemo(() => {
    if (!rankingCollection?.collectionItems) return [];
    const products = rankingCollection.collectionItems.map((item) => item.product);
    return products.filter((product) => product !== undefined) as Product[];
  }, [rankingCollection?.collectionItems]);

  const handleCollectionSelection = useCallback(
    (key: string) => {
      getProductCollectionProducts(key)
        .then((productCollection) => {
          if (productCollection.collectionItems) {
            setRankingCollection(productCollection);
          }
        })
        .catch((error) => {
          console.error(error);
        });
    },
    [getProductCollectionProducts, setRankingCollection]
  );

  const handleClickProductItem = (product: Product) => {
    logAction({
      eventType: 'CLICK',
      eventTarget: 'HOME_PRODUCT',
      eventCategory: 'product',
      eventData: extractProductEventData(product, {
        type: 'main-ranking',
        slug: rankingCollection?.slug ?? '',
        name: rankingCollection?.name ?? '',
      }),
    });
  };

  const handleLikeProduct = (product: Product) => {
    if (!product.isLiked) {
      logAction({
        eventType: 'CLICK',
        eventTarget: 'HOME_PRODUCT_LIKE',
        eventCategory: 'product',
        eventData: extractProductEventData(product, {
          type: 'main-ranking',
          slug: rankingCollection?.slug ?? '',
          name: rankingCollection?.name ?? '',
        }),
      });
    }
  };

  const skeleton = (
    <>
      <RankingButtonsSkeleton />
      <Container px={'20px'} pt={'12px'} stackProps={{ column: true, gap: '8px' }}>
        <RankingSkeleton />
        <RankingSkeleton />
        <RankingSkeleton />
        <RankingSkeleton />
      </Container>
    </>
  );

  if (!collectionGroup || !buttonInfos) {
    return skeleton;
  }

  return (
    <>
      <Container px={'20px'}>
        <Typography variant={'h5'}>{collectionGroup.name}</Typography>
        <Container sx={{ my: 1 }}>
          <ChipButtonGroup buttonInfos={buttonInfos} onChange={handleCollectionSelection} />
        </Container>
      </Container>
      {rankingProducts.length === 0 ? (
        <Container px={'20px'} pt={'12px'} stackProps={{ column: true, gap: '8px' }}>
          <RankingSkeleton />
          <RankingSkeleton />
          <RankingSkeleton />
          <RankingSkeleton />
        </Container>
      ) : (
        <Container py={'4px'}>
          <ProductLimitedList
            products={rankingProducts}
            row={4}
            column={2}
            numbering
            onClickProductItem={(product) => handleClickProductItem(product)}
            onLikeProductItem={(product) => handleLikeProduct(product)}
          />
        </Container>
      )}
    </>
  );
}

function RankingSkeleton() {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
      }}
    >
      <Skeleton width={120} height={120} variant={'rounded'} />
      <Box height={'100%'} flex={1}>
        <Skeleton variant={'text'} width={'100%'} height={30} animation={'wave'} />
        <Skeleton variant={'text'} width={'100%'} height={30} animation={'wave'} />
        <Skeleton variant={'text'} width={'75%'} height={30} animation={'wave'} />
        <Skeleton variant={'text'} width={'40%'} height={30} animation={'wave'} />
      </Box>
    </Box>
  );
}

function RankingButtonsSkeleton() {
  return (
    <Container stackProps={{ column: true, gap: '8px' }} py={'8px'}>
      <Container px={'20px'}>
        <Skeleton variant={'rounded'} width={'65%'} height={'20px'} />
      </Container>
      <Container px={'20px'} stackProps={{ row: true, gap: '8px' }}>
        <Skeleton variant={'rounded'} width={'40%'} height={'32px'} sx={{ borderRadius: '20px' }} />
        <Skeleton variant={'rounded'} width={'30%'} height={'32px'} sx={{ borderRadius: '20px' }} />
        <Skeleton variant={'rounded'} width={'40%'} height={'32px'} sx={{ borderRadius: '20px' }} />
      </Container>
      <Container px={'20px'} stackProps={{ row: true, gap: '8px' }}>
        <Skeleton variant={'rounded'} width={'30%'} height={'32px'} sx={{ borderRadius: '20px' }} />
        <Skeleton variant={'rounded'} width={'40%'} height={'32px'} sx={{ borderRadius: '20px' }} />
        <Skeleton variant={'rounded'} width={'40%'} height={'32px'} sx={{ borderRadius: '20px' }} />
      </Container>
    </Container>
  );
}
